import { JobLineItem, JobPayment, JobV2 } from "../@types/job";
import { isServiceLineItem } from "./lineItem";
import { calculateJob } from "./math";
import { calculatePaymentStatus } from "./paymentOperations";

// Identifiers only ever written by a webhook or a server-side backfill. The
// edit form never populates them, so a form copy that lacks one is stale
// rather than cleared, and the live value wins.
const WEBHOOK_OWNED_PAYMENT_FIELDS = [
  "qbPaymentId",
  "stripeTransactionId",
] as const;

export interface StaleJobPaymentReconciliation {
  // Baseline the caller should diff against: the form's snapshot with anything
  // that landed in Firestore afterwards folded back in.
  currentJob: JobV2;
  // Form payload to write, with the same rescues applied and totals redone.
  updatedJobForm: JobV2;
  rescuedPayments: JobPayment[];
  rescuedLineItems: JobLineItem[];
}

const isProcessingFeeLineItem = (lineItem: JobLineItem): boolean =>
  isServiceLineItem(lineItem) && !!lineItem.service?.includes("Processing Fee");

// Carry webhook-owned ids forward onto the form's copy of a payment it already
// knew about, so a save that predates the webhook does not blank them out.
const preserveWebhookOwnedFields = (
  formPayment: JobPayment,
  livePayment: JobPayment | undefined
): JobPayment => {
  if (!livePayment) return formPayment;

  let merged: JobPayment | null = null;
  for (const field of WEBHOOK_OWNED_PAYMENT_FIELDS) {
    if (livePayment[field] && !formPayment[field]) {
      merged = { ...(merged || formPayment), [field]: livePayment[field] };
    }
  }
  return merged || formPayment;
};

/**
 * updateJobV2 writes the whole job document from the edit form's local state.
 * When a payment reaches Firestore after that form was loaded (the take-payment
 * modal, the POS, the client portal, a Stripe or QuickBooks webhook), the form
 * has never seen it, and saving an unrelated edit silently erases it.
 *
 * A payment missing from the form is only a genuine deletion when the form's
 * own baseline had it too. Anything live but absent from BOTH the baseline and
 * the form arrived after the form loaded, so it is rescued into each side:
 *
 *  - into the form, so the write preserves it;
 *  - into the baseline, so the history diff and the "did this save add a
 *    payment?" checks that drive QuickBooks treat it as pre-existing instead of
 *    re-creating a payment that QuickBooks already holds.
 *
 * Line items are only rescued when they are the processing-fee service line
 * that came in with a rescued payment; every other line item stays the form's
 * to decide, since that is what the user was editing.
 */
export const reconcileStaleJobPayments = (
  liveJob: JobV2 | null | undefined,
  currentJob: JobV2,
  updatedJobForm: JobV2
): StaleJobPaymentReconciliation => {
  const unchanged: StaleJobPaymentReconciliation = {
    currentJob,
    updatedJobForm,
    rescuedPayments: [],
    rescuedLineItems: [],
  };

  if (!liveJob) return unchanged;

  const livePayments = liveJob.payments || [];
  const baselinePayments = currentJob.payments || [];
  const formPayments = updatedJobForm.payments || [];

  const baselinePaymentUids = new Set(baselinePayments.map((p) => p.uid));
  const formPaymentUids = new Set(formPayments.map((p) => p.uid));

  const rescuedPayments = livePayments.filter(
    (livePayment) =>
      !baselinePaymentUids.has(livePayment.uid) &&
      !formPaymentUids.has(livePayment.uid)
  );

  const livePaymentsByUid = new Map(livePayments.map((p) => [p.uid, p]));
  const mergedFormPayments = formPayments.map((formPayment) =>
    preserveWebhookOwnedFields(formPayment, livePaymentsByUid.get(formPayment.uid))
  );
  const preservedWebhookFields = mergedFormPayments.some(
    (payment, index) => payment !== formPayments[index]
  );

  if (rescuedPayments.length === 0) {
    // Nothing was clobbered, but a webhook id may still need carrying forward.
    return preservedWebhookFields
      ? {
          ...unchanged,
          updatedJobForm: { ...updatedJobForm, payments: mergedFormPayments },
        }
      : unchanged;
  }

  const baselineLineItemUids = new Set(
    (currentJob.lineItems || []).map((item) => item.uid)
  );
  const formLineItemUids = new Set(
    (updatedJobForm.lineItems || []).map((item) => item.uid)
  );

  const rescuedLineItems = (liveJob.lineItems || []).filter(
    (liveLineItem) =>
      isProcessingFeeLineItem(liveLineItem) &&
      !baselineLineItemUids.has(liveLineItem.uid) &&
      !formLineItemUids.has(liveLineItem.uid)
  );

  const lineItems = [...(updatedJobForm.lineItems || []), ...rescuedLineItems];
  const payments = [...mergedFormPayments, ...rescuedPayments];

  const includeTax = updatedJobForm.includeTax;
  const effectiveTaxRate = includeTax ? updatedJobForm.taxRate || 0 : 0;

  // totalDeposit stays the form's: it is derived from line-item deposits, and
  // the rescued lines are processing fees, which carry none.
  const grandTotal = calculateJob
    .grandTotal(lineItems, effectiveTaxRate, includeTax)
    .raw();
  const remainingBalance = calculateJob
    .remainingBalance(lineItems, effectiveTaxRate, payments, includeTax)
    .raw();

  const reconciledForm: JobV2 = {
    ...updatedJobForm,
    lineItems,
    payments,
    subtotal: calculateJob.subtotal(lineItems).raw(),
    totalDiscount: calculateJob.totalDiscount(lineItems).raw(),
    subtotalAfterDiscounts: calculateJob
      .subtotalAfterDiscounts(lineItems)
      .raw(),
    totalTax: calculateJob.tax(lineItems, effectiveTaxRate, includeTax).raw(),
    grandTotal,
    remainingBalance,
    paymentStatus: calculatePaymentStatus(
      payments,
      updatedJobForm.totalDeposit,
      grandTotal,
      remainingBalance
    ),
  };

  // The baseline was stale for exactly the fields the rescue touches, so take
  // those from the live document. Everything else stays the form's snapshot,
  // which is what stage emails and part moves are diffed against.
  const reconciledCurrentJob: JobV2 = {
    ...currentJob,
    lineItems: [...(currentJob.lineItems || []), ...rescuedLineItems],
    payments: [...baselinePayments, ...rescuedPayments],
    subtotal: liveJob.subtotal,
    totalDiscount: liveJob.totalDiscount,
    subtotalAfterDiscounts: liveJob.subtotalAfterDiscounts,
    totalTax: liveJob.totalTax,
    grandTotal: liveJob.grandTotal,
    remainingBalance: liveJob.remainingBalance,
    paymentStatus: liveJob.paymentStatus,
  };

  return {
    currentJob: reconciledCurrentJob,
    updatedJobForm: reconciledForm,
    rescuedPayments,
    rescuedLineItems,
  };
};
